/**
 * LES ACCESSOIRES D'UN APPAREIL, LUS DANS LES LIENS (spec 002, récit 5, EF-035 — 04/11/2026).
 *
 * Deux sources disaient la même chose : le texte libre `accessoires` de la fiche (« PC ») et les liens
 * `accessoire_de` (accessoire → appareil servi). Pour l'UriSed Mini (EQ22), le texte disait « PC », les liens en
 * comptaient cinq. LA SOURCE EST LE LIEN ; le texte reste consultable, nommé « ancienne saisie », tant qu'il dit
 * autre chose que les liens. Module pur (testé depuis `backend/tests`).
 */
import type { GedLien } from './types';

export interface AccessoireLie { code: string; nom: string }

/** Les accessoires reliés à l'appareil `code` (liens `accessoire_de` dont il est la cible), triés par code. */
export function accessoiresDe(
  liens: Pick<GedLien, 'typeLien' | 'sourceType' | 'sourceRef' | 'cibleType' | 'cibleRef'>[],
  code: string,
  nomDe: (code: string) => string,
): AccessoireLie[] {
  if (!code) return [];
  const vus = new Set<string>();
  const out: AccessoireLie[] = [];
  for (const l of liens || []) {
    if (l.typeLien !== 'accessoire_de' || l.cibleType !== 'equipement' || l.sourceType !== 'equipement') continue;
    if (l.cibleRef !== code || !l.sourceRef || vus.has(l.sourceRef)) continue;
    vus.add(l.sourceRef);
    out.push({ code: l.sourceRef, nom: nomDe(l.sourceRef) || '' });
  }
  return out.sort((a, b) => a.code.localeCompare(b.code, 'fr', { numeric: true }));
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Le texte libre est-il à montrer en « ancienne saisie » ? Oui s'il n'est pas vide et ne dit pas exactement la même
 * chose que les liens (mêmes codes ou mêmes désignations, dans n'importe quel ordre, séparés par virgule ou point-virgule).
 */
export function ancienneSaisieDiffere(texte: string | null | undefined, accessoires: AccessoireLie[]): boolean {
  const t = norm(String(texte || ''));
  if (!t) return false;
  const morceaux = t.split(/\s*[,;]\s*/).filter(Boolean).sort();
  const parCode = accessoires.map((a) => norm(a.code)).sort();
  const parNom = accessoires.map((a) => norm(a.nom)).sort();
  const egal = (x: string[]) => x.length === morceaux.length && x.every((v, i) => v === morceaux[i]);
  return !(egal(parCode) || egal(parNom));
}
